import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

/**
 * Variants are expressed via semantic tokens so they look correct in both
 * themes without any `dark:` overrides on the call site.
 *
 * April 2026 polish
 * ─────────────────
 *   - Primary now gets a two-layer shadow (sm + elev-1) that doubles as
 *     a subtle drop-shadow under the button mass. On :hover the layer
 *     brightens ~2px and the shadow stretches — feels like a physical
 *     button lifting off the page.
 *   - Secondary picks up `.lift` on hover so ghost-on-card combos feel
 *     alive instead of flat.
 *   - Press feedback keeps the scale(0.97) via `.pressable` plus a 10ms
 *     quicker curve (`ease-out-strong`) — it should feel instant.
 */
const variants: Record<Variant, string> = {
  primary: cn(
    'bg-ink text-surface shadow-xs',
    'hover:bg-ink/95 hover:shadow-elev-1',
    'disabled:bg-ink/40 disabled:text-surface/70 disabled:shadow-none',
  ),
  secondary: cn(
    'bg-surface-2 text-ink border border-line shadow-xs',
    'hover:bg-surface-3 hover:border-line-strong',
    'disabled:opacity-50',
  ),
  ghost: cn(
    'text-ink-muted',
    'hover:text-ink hover:bg-surface-3',
    'disabled:opacity-50',
  ),
  danger: cn(
    'bg-err text-white shadow-xs',
    'hover:bg-err/90 hover:shadow-elev-1',
    'disabled:opacity-50',
  ),
};

const sizes: Record<Size, string> = {
  // Wave mobile-audit · sm bumped from h-9 to h-11 (44px) for touch targets.
  // Tradeoff: denser layouts visually shift ~5px taller, but touch always wins.
  // If a specific callsite needs the old 36px density (tags, chips inside
  // cards), use a custom className override rather than reintroducing a 'xs'.
  sm: 'h-11 min-h-11 px-3 text-sm rounded-lg',
  md: 'h-11 px-4 text-[15px] rounded-xl',
  lg: 'h-12 px-5 text-base rounded-xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  // Wave 317-prep · Anderson feedback (RDO foto fechava o RDO):
  // HTML5 default for `<button>` inside `<form>` is type="submit". O Button
  // não declarava type, então qualquer Button dentro de um <form> (ex:
  // PhotoUploader → "Adicionar fotos") submetia o form ao clicar. Agora
  // padrão é "button"; quem precisa submit explicitamente passa
  // `type="submit"` (28 callsites já fazem isso e seguem funcionando).
  { className, variant = 'primary', size = 'md', type = 'button', loading, fullWidth, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'pressable focus-ring inline-flex select-none items-center justify-center gap-2 font-medium',
        'transition-[transform,background-color,border-color,color,box-shadow,filter] duration-160 ease-out-strong',
        'disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden
        />
      )}
      {children}
    </button>
  );
});
